<script setup lang="ts">
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import QuizEngine from "~/features/computer/components/QuizEngine.vue";
import { variablesExercises } from "~/features/computer/data/exercises/variables.exercises";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { ch2Questions } from "~/features/python/ch2/exercise/data/ch2.questions";
import VariableNameChecker from "~/features/python/ch2/variables/components/VariableNameChecker.vue";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const relatedCards = pythonInteractiveCards.filter((item) => item.to !== "/11th/computer/python/variables/");
const variableQuestions = ch2Questions.filter(
  (question) => question.topic === "variables" || question.concepts.includes("variables"),
);

usePageSeo({
  title: "Chapter 2 Python Variables | Ustaad",
  description: "Chapter 2 Python variables with naming rules, solved MCQs, short answers and board-style questions for class 11 students.",
  keywords: "chapter 2 python variables, python variables class 11, 11th class computer variables, first year computer python variables",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Variables", item: "/11th/computer/python/variables/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Variables",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python Data Types", path: "/11th/computer/python/data-types/" },
      { name: "Chapter 2 Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Chapter 2 Python Short Questions", path: "/11th/computer/python/short-questions/" },
      { name: "Chapter 2 Python Programs", path: "/11th/computer/python/programs/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">Python Chapter 2</p>
        <h1 class="mt-2 text-2xl font-bold tracking-tight">Chapter 2 Python Variables</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">
          Yahan aap samjhoge ke variable aik dabbe ki tarah hota hai jisme hum cheez rakh dete hain. Phir Python us cheez ko baad me use karta hai.
        </p>
      </section>

      <section class="grid gap-6 lg:grid-cols-2">
        <article class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Sab se pehle yeh samjho</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- Variable ek named storage hota hai</p>
            <p>- Variable name number se start nahi hota</p>
            <p>- Space aur Python keywords variable name me allowed nahi hotay</p>
          </div>
        </article>

        <article class="rounded-3xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/20">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Is page par kya seekhna hai</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- Valid aur invalid variable names</p>
            <p>- Assignment operator `=` ka role</p>
            <p>- Variable ke through value print karna</p>
          </div>
        </article>
      </section>

      <section class="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <VariableNameChecker />

        <article class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Asaan rules</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- Letter ya underscore se start karein</p>
            <p>- Number start me nahi aata</p>
            <p>- Space aur `#`, `-`, `@` jaise special characters avoid karein</p>
            <p>- `for`, `if`, `def` jaise Python keywords use na karein</p>
          </div>
        </article>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Variables ke chhote sawal</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Ab dekhte hain ke aapko idea samajh aaya ya nahi.</p>
        </div>
        <div class="mt-4">
          <QuizEngine :questions="variablesExercises.mcqs" />
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Chhota jawab</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Yeh short line yaad rakhne se concept aur clear hota hai.</p>
        </div>
        <div class="mt-4 grid gap-3">
          <article
            v-for="item in variablesExercises.short"
            :key="item.id"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">{{ item.question }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Ab bade sawal dekho</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Yahan click karke aap variable wale bade program sawal kar sakte ho.</p>
        </div>

        <div class="mt-4 grid gap-3">
          <NuxtLink
            v-for="question in variableQuestions"
            :key="question.id"
            :to="`/11th/computer/python/programs/#q-${question.id}`"
            class="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-sky-300 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Q{{ question.number }}. {{ question.question }}</h3>
              <span class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                {{ question.type }}
              </span>
            </div>
          </NuxtLink>
        </div>
      </section>

      <InteractiveTopics
        eyebrow="More Python Topics"
        title="More Python Topics"
        intro="Variables ke baad data types, operators, loops, functions aur programs bhi yahan se khol sakte hain."
        :cards="relatedCards"
      />
    </div>
  </PageContainer>
</template>
